import React, { useEffect, useRef, useState } from 'react';
import { Countdown, CountdownItem, Digit, EndMessage, Unit, Wrapper } from './styled';
import { useStyle } from '../../../hooks/useStyle';
import { Duration, intervalToDuration } from 'date-fns';
import Distance from '../../../styled/Distance';

const durationIsSet = (duration: Duration) =>
  duration.years !== undefined ||
  duration.months !== undefined ||
  duration.days !== undefined ||
  duration.hours !== undefined ||
  duration.minutes !== undefined ||
  duration.seconds !== undefined;

export interface ICountdownSlice {
  backgroundColor: string;
  endMessage?: string;
  largeScreenImageSrc?: string;
  largeScreenImageWidth: string;
  smallScreenImageSrc?: string;
  smallScreenImageWidth: string;
  textColor: string;
  timestamp?: number;
}

const CountdownSlice = ({
  backgroundColor,
  endMessage,
  textColor,
  timestamp,
  largeScreenImageSrc,
  largeScreenImageWidth,
  smallScreenImageSrc,
  smallScreenImageWidth,
}: ICountdownSlice) => {
  const interval = useRef<ReturnType<typeof setInterval>>();
  const wrapperStyle = useStyle({ backgroundColor, color: textColor });
  const largeScreenImageStyle = useStyle({ width: largeScreenImageWidth });
  const smallScreenImageStyle = useStyle({ width: smallScreenImageWidth });
  const end = new Date(timestamp || 0).getTime();
  const now = new Date().getTime();
  const passed = now > end;
  const [diff, setDiff] = useState<Duration | null>(passed ? null : {});

  useEffect(() => {
    interval.current = setInterval(() => {
      setDiff(calcDiff(end));
    }, 1000);

    return () => {
      clearInterval(interval.current);
    };
  }, [end]);

  if (passed && !endMessage) {
    return null;
  }

  return (
    <Wrapper style={wrapperStyle}>
      {largeScreenImageSrc && (
        <img src={largeScreenImageSrc} style={largeScreenImageStyle} className="hidden-lt-md" />
      )}
      {smallScreenImageSrc && (
        <img src={smallScreenImageSrc} style={smallScreenImageStyle} className="hidden-md" />
      )}

      {diff && largeScreenImageSrc && <Distance size="2" />}

      {diff && (
        <Countdown className={durationIsSet(diff) ? undefined : 'pending'}>
          <CountdownItem>
            <Digit>{diff.days || '0'}</Digit>
            <Unit>dagar</Unit>
          </CountdownItem>
          <CountdownItem>
            <Digit>{diff.hours || '0'}</Digit>
            <Unit>timmar</Unit>
          </CountdownItem>
          <CountdownItem>
            <Digit>{diff.minutes || '0'}</Digit>
            <Unit>minuter</Unit>
          </CountdownItem>
          <CountdownItem>
            <Digit>{diff.seconds || '0'}</Digit>
            <Unit>sekunder</Unit>
          </CountdownItem>
        </Countdown>
      )}

      {endMessage && passed && <EndMessage>{endMessage}</EndMessage>}
    </Wrapper>
  );
};

export default CountdownSlice;

const calcDiff = (end?: number) => {
  if (!end) {
    return null;
  }

  const start = new Date().getTime();

  if (start >= end) {
    return null;
  }

  return intervalToDuration({
    start,
    end,
  });
};
